/**
 * gdrive_import - Import a Google Drive file into the workspace
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { googleDriveAdapter } from '../../services/integrations/adapters/GoogleDriveAdapter';
import { GoogleDriveErrorHandler } from '../../services/integrations/adapters/GoogleDriveErrorHandler';
import { useSpaceStore } from '../../stores/spaceStore';

export const gdrive_import: SimpleDocCommand = {
  id: 'gdrive_import',
  logicalName: 'gdrive_import',
  version: 'v1',
  
  description: `Import a Google Drive file into the workspace as an editable entity.

**PREREQUISITES:**
- Google Drive must be connected
- File ID must be obtained from gdrive_search

**WHAT IT DOES:**
1. Reads content from Google Drive file
2. Converts to native workspace entity:
   - Google Docs → Note entity
   - Google Sheets → Sheet entity
   - Google Slides → Presentation entity (view-only)
   - PDFs → File entity
3. Creates editable copy in workspace
4. Original file remains in Google Drive (not modified)

**PARAMETERS:**
- fileId: Google Drive file ID (from gdrive_search)
- spaceId: Optional - Space to import into (defaults to current space)

**CONVERSION MAPPING:**
- Google Doc → Note (fully editable)
- Google Sheet → Sheet (fully editable)
- Google Slides → Presentation (view-only for now)
- PDF → File entity (viewable, not editable)
- Images → File.Image entity (viewable)

**EXAMPLES:**

Import a Google Doc into current space:
{ "fileId": "1abc123xyz..." }

Import a Google Sheet into specific space:
{ "fileId": "1def456uvw...", "spaceId": "space_12345" }

**WORKFLOW:**
Step 1: Search with gdrive_search
Step 2: Review file in results
Step 3: Import with gdrive_import
Step 4: Edit imported entity in workspace

**IMPORTANT NOTES:**
- Creates a copy in workspace (original Drive file is untouched)
- Imported entity is independent of the Drive file
- Changes to the imported entity do NOT sync back to Drive
- To keep files synced, use gdrive_read repeatedly instead
- Re-importing the same file returns the existing entity (deduplication is automatic)

**LIMITS:**
- Max file size: 50MB
- Large files may take time to import`,

  schema: {
    parameters: [
      {
        name: 'fileId',
        type: 'string',
        description: 'Google Drive file ID',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim() === '') {
            return 'File ID is required';
          }
          return null;
        }
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Target space ID (defaults to current space)',
        required: false
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { fileId, spaceId } = params;
    
    console.log('[gdrive_import] Importing file:', fileId);
    
    try {
      const { default: useEntityStore } = await import('../../stores/entityStore');
      const entities = useEntityStore.getState().entities;

      const driveIntegrations = entities.filter((e: any) =>
        (e.entityType === 'Integration.GoogleDrive' || e.entityType === 'Integration') &&
        e.skeleton?.integrationType === 'GoogleDrive' &&
        e.skeleton?.status === 'connected' &&
        !e.trashed
      );
      
      if (driveIntegrations.length === 0) {
        return {
          status: 'error',
          message: 'Google Drive not connected',
          error: { code: 'not_connected', category: 'validation', retryable: false },
          format: 'plaintext'
        };
      }

      const driveIntegration = driveIntegrations.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      
      const targetSpaceId = spaceId || useSpaceStore.getState().activeSpace?.id;
      
      if (!targetSpaceId) {
        return {
          status: 'error',
          message: 'No active space found. Please specify a spaceId or ensure a space is open.',
          error: { code: 'no_active_space', category: 'validation', retryable: false },
          format: 'plaintext'
        };
      }
      
      const result = await googleDriveAdapter.importFile(
        driveIntegration._id,
        fileId,
        targetSpaceId
      );
      
      console.log('[gdrive_import] Successfully imported:', result.entityId);
      
      return {
        status: 'success',
        message: result.message,
        data: {
          entityId: result.entityId,
          entityType: result.entityType,
          fileId: fileId,
          spaceId: targetSpaceId
        },
        format: 'json'
      };
      
    } catch (error: any) {
      const errorInfo = GoogleDriveErrorHandler.classifyError(error);
      
      // Map error categories to StandardToolResult categories
      const categoryMap: Record<string, 'validation' | 'execution' | 'timeout' | 'permission' | 'system' | 'cancelled'> = {
        'configuration': 'validation',
        'network': 'execution',
        'api': 'execution',
        'auth': 'permission',
        'validation': 'validation'
      };
      
      const standardCategory = categoryMap[errorInfo.category] || 'execution';
      
      console.error('[gdrive_import] Error:', {
        code: errorInfo.errorCode,
        category: errorInfo.category,
        standardCategory,
        retryable: errorInfo.retryable,
        originalError: error
      });
      
      return {
        status: 'error',
        message: errorInfo.userMessage,
        error: {
          code: errorInfo.errorCode,
          category: standardCategory,
          retryable: errorInfo.retryable,
          details: errorInfo.details
        },
        format: 'plaintext'
      };
    }
  }
};
